// Klasse für die Ablage global nutzbarer Interfaces und Typen, analog der Nutzungsweise des TypeScript Teams
// https://github.com/Microsoft/TypeScript/blob/main/src/compiler/types.ts

/**
 * Lokalisierter Text vorerst als einfaches string -> string mapping abbgebildet.
 *
 * FIXME: Typisierung könnte strikter gemacht werden, wenn sichergestellt werden kann,
 *  dass das aktuell ausgewählte locale immer nur "de" oder "en" ist.
 */
export type LocalizedText = Record<string, string>;

export interface ReactRouterParamTypes {
    submissionId: string;
    reviewId: string;
}

// Interface für ein Kontaktdaten-Objekt
export interface Contact {
    salutation: string;
    title?: string;
    firstName: string;
    lastName: string;
    street: string;
    streetAdditional?: string;
    houseno: string;
    postalCode: string;
    city: string;
    countryISOCode: string;
    country?: string;
    email: string;
    phone: string;
    mobilePhone?: string;
    fax?: string;
    birthDate?: string;
    fullCompany?: string;
    dialogCountryCode?: string;
    company: string;
    company2?: string;
    company3?: string;
    function?: string;
}

// Interface für ein Themengebiet-Objekt
export interface Topic {
    id: string;
    name: string;
    label: LocalizedText;
    updatedAt: string;
    createdAt: string;
}

// Interface für ein Thema-Objekt
export interface Theme {
    id: string;
    name: string;
    label: LocalizedText;
    updatedAt: string;
    createdAt: string;
}

export interface EventSeries {
    id: string;
    name: string;
}

// Interface für ein CallForPapers-Objekt
export interface CallForPapers {
    isPublishedVersion: string;
    isWorkingVersion: string;
    lastPublishAt: string;
    id: string;
    internalName: string;
    internalShortName: string;
    publicName: LocalizedText;
    urlSlug: string;
    enabledLanguages: {
        de: boolean;
        en: boolean;
    },
    emailFromAddress?: string;
    emailReplyToAddress?: string;
    emailBlindCopyAddress: string;
    internalNote?: string;
    isEnabled: boolean;
    submissionWindow: {
        from: string;
        until: string;
    }
    editDeadline: string;
    reviewDeadline: string;
    form: CallForPaperForm;
    versionContainer: {
        id: string;
        event: {
            id: string;
            name: string;
        }
        createdAt: string;
        updatedAt: string;
    },
    styling: object;
    changedSincePublish: boolean;
    topics: Topic[];
    reviewCriteria: string[];
    themes: Theme[];
    templateContainer: object;
    updatedAt: string;
    createdAt: string;
}

// Interface für ein CallForPaperForm-Objekt
export interface CallForPaperForm {
    id: string;
    allowPartialSubmissions: boolean;
    widgets: FormWidget[];
}

// Interface für ein CallForPaper-Formular-Widget-Objekt
export interface FormWidget {
    id: string;
    active: boolean;
    isVisibleForReview: boolean;
    backendLabel: string;
    deactivatable: boolean;
    baseWidget: boolean;
    deletable: boolean;
    label: LocalizedText;
    type: string;
    required?: boolean;
}

// Interface für ein PaperSubmission-Objekt
export interface PaperSubmission {
    id: string;
    eventNumber?: string;
    title?: string;
    subTitle?: string;
    abstract?: string;
    topic?: Topic | undefined,
    themes?: Theme[],
    presentationLanguage?: string;
    logoImage?: object;
    callForPapers: CallForPapers;
    submittedBy: {
        id: string;
        billingContact: Contact;
        phoneIfOptin?: string;
        mainContact: Contact;
        additionalData: [];
        language: string;
        type?: string;
        sortWeight?: number
    },
    speaker: {
        billingContact: Contact;
        phoneIfOptin?: string;
        mainContact: Contact;
        additionalData: [];
        language: string;
        type: string;
        id: string;
        sortWeight?: number;
    },
    decision: PCOStatus;
    mailSentAt?: string;
    updatedAt: string;
    createdAt: string;
    formData: PaperSubmissionFormDatum[];
    tasks: TaskProps[];
    isValidSubmission: boolean;
    otherPersonReferences?: {
        person: Person;
        role?: {
            id: string,
            label: {
                [key: string]: string
            }
        };
    }[];
    otherPersons?: {
        person: Person;
        role?: {
            id: string,
            label: {
                [key: string]: string
            }
        };
    }[];
    pcoPlus?: PaperSubmissionPcoPlus
}

// FIXME: erweitern um die Status aus W2-1174
// String Union der verschiedenen PCO Status (Entscheidungen)
export type PCOStatus =
    | 'SUBMISSION_NO_DECISION'
    | 'SUBMISSION_ACCEPTED'
    | 'SUBMISSION_DECLINED'
    | 'SUBMISSION_POTENTIALLY_ACCEPTED';

export type PCOPlusSessionStatus =
    | 'PCO_PLUS_STATUS_ORGANISED'
    | 'PCO_PLUS_STATUS_CHANGE'
    | 'PCO_PLUS_STATUS_CANCELED'
    | 'PCO_PLUS_STATUS_CANCELED_BEFORE_PUBLISHING';

// Map von PCO-PLUS-Status auf i18n-Keys
export const PCO_PLUS_STATUS_MAP = {
    'PCO_PLUS_STATUS_ORGANISED': 'myarea.sessions.status.organised',
    'PCO_PLUS_STATUS_CHANGE': 'myarea.sessions.status.change',
    'PCO_PLUS_STATUS_CANCELED': 'myarea.sessions.status.canceled',
    'PCO_PLUS_STATUS_CANCELED_BEFORE_PUBLISHING': 'myarea.sessions.status.canceled_before_publish'
};

interface RequestedDateTimePeriod {
    startDate: Date,
    endDate: Date
}

export interface PaperSubmissionPcoPlus {
    requestedDateTimePeriod?: RequestedDateTimePeriod,
    presentationCount: number | undefined,
    presentationType: string | undefined,
    organizerRegiNumber: number | undefined
}

// Interface für ein PaperSubmissionFormDatum-Objekt
export interface PaperSubmissionFormDatum {
    id: string
    type: string;
    key: string;
    value: any
}

// Interface für ein Task-Objekt
export interface TaskProps {
    id: string;
    task: string;
    isActive: boolean;
}

export interface Person {
    id: string;
    mainContact: {
        company: string;
        function: string;
        salutation: string;
        title: string;
        firstName: string;
        lastName: string;
        street: string;
        houseno: string;
        postalCode: string;
        city: string;
        countryISOCode: string;
        country: string;
        email: string;
        phone: string;
    }
    pcoPersonData: {
        homepageUrl: string;
        vita: string;
        image: string;
        photographer: string;
        imageCaption: string;
    }
}

export interface PaperSubmissionReview {
    id?: string;
    submission?: PaperSubmission;
    reviewer?: {
        mainContact: Contact
    };
    eventReviewConfiguration?: EventReviewConfiguration;
    criteriaScores?: CriteriaScore[];
    reasoning?: string;
}

export interface EventReviewConfiguration {
    id: string;
    event: object;
    criteria: EventReviewCriterium[];
}

export interface EventReviewCriterium {
    id?: string;
    name: string;
}

export interface CriteriaScore {
    criteriumId: string;
    score?: number;
    note?: string;
}

export interface ListingCriteriaScore {
    criterium: string;
    score: number;
}
